/**
 * Linked posts (Paul, 2026-10-21).
 *
 * A post with `linkedPartKey` is a window onto another post: Department 19's "Office of the CFO"
 * shows whoever holds the top-row Chief Financial Officer post. It owns no people, hat or
 * permissions — the board just displays the target's holders through the existing
 * held-from-above fields (so the chip's click already navigates to the target). When the target is
 * vacant, the linked post shows the target's OWN held-from-above. People assigned on the linked
 * post itself win, a missing/self/chained link is ignored (the normal HFA result stands).
 *
 * Run AFTER the normal held-from-above pass, over every post on the board at once.
 */
export interface LinkablePost {
    partKey?: string;
    key?: string;
    name?: string;
    linkedPartKey?: string | null;
    userDetailsArray?: Array<{ key?: string; name?: string; isPending?: boolean } | null | undefined>;
    heldFromAboveUid?: string;
    heldFromAboveUserName?: string;
    heldFromAboveColor?: string;
    heldFromAbovePartKey?: string;
    linkedToPartKey?: string;
    linkedToName?: string;
}

const activeUsers = (p: LinkablePost) => (p.userDetailsArray || []).filter((u) => !!u && !u.isPending);

export function applyLinkedPosts(posts: Array<LinkablePost | null | undefined>): void {
    const list = (posts || []).filter((p): p is LinkablePost => !!p && typeof p === 'object');
    const byKey = new Map<string, LinkablePost>();
    for (const p of list) {
        const k = p.partKey || p.key;
        if (k) byKey.set(k, p);
    }

    // Snapshot every target's own HFA first, so applying one link never feeds another.
    const own = new Map<
        LinkablePost,
        Pick<LinkablePost, 'heldFromAboveUid' | 'heldFromAboveUserName' | 'heldFromAboveColor' | 'heldFromAbovePartKey'>
    >();
    for (const p of list) {
        own.set(p, {
            heldFromAboveUid: p.heldFromAboveUid,
            heldFromAboveUserName: p.heldFromAboveUserName,
            heldFromAboveColor: p.heldFromAboveColor,
            heldFromAbovePartKey: p.heldFromAbovePartKey,
        });
    }

    for (const p of list) {
        p.linkedToPartKey = undefined;
        p.linkedToName = undefined;
        const selfKey = p.partKey || p.key;
        const targetKey = p.linkedPartKey;
        if (!targetKey || targetKey === selfKey) continue;
        if (activeUsers(p).length > 0) continue;
        const target = byKey.get(targetKey);
        if (!target || target.linkedPartKey) continue;

        const holders = activeUsers(target);
        if (holders.length > 0) {
            p.heldFromAboveUid = holders.map((u) => u?.key || '').join(',');
            p.heldFromAboveUserName = holders.map((u) => u?.name || '').join(', ');
            p.heldFromAboveColor = own.get(target)?.heldFromAboveColor;
            p.heldFromAbovePartKey = target.partKey || target.key;
        } else {
            Object.assign(p, own.get(target));
        }
        p.linkedToPartKey = target.partKey || target.key;
        p.linkedToName = target.name || '';
    }
}

type PartLike =
    | {
          partKey?: string;
          name?: string;
          partType?: string;
          parentPartKey?: string;
          linkedPartKey?: string | null;
      }
    | null
    | undefined;

export interface LinkOrigin {
    /** The post this one represents. */
    partKey: string;
    name: string;
    /** Names from the top of the board down to (and including) the represented post. */
    path: string[];
}

/**
 * What a linked post represents and where that post sits (Paul, 2026-10-21: a linked post "should
 * just show the representation and origin" — links are set only by the migration, never in the UI).
 * Null for an unlinked post, an unknown post, a missing/self/chained target.
 */
export function linkOrigin(orgParts: PartLike[] | null | undefined, partKey: string | undefined): LinkOrigin | null {
    const parts = (orgParts || []).filter((p): p is NonNullable<PartLike> => !!p && !!p.partKey);
    const byKey = new Map(parts.map((p) => [p.partKey as string, p]));
    const self = partKey ? byKey.get(partKey) : undefined;
    const targetKey = self?.linkedPartKey;
    if (!self || !targetKey || targetKey === partKey) return null;
    const target = byKey.get(targetKey);
    if (!target || target.linkedPartKey) return null;

    const path: string[] = [];
    const seen = new Set<string>();
    for (let p: NonNullable<PartLike> | undefined = target; p && !seen.has(p.partKey as string); ) {
        seen.add(p.partKey as string);
        path.unshift((p.name || '').trim() || 'Unnamed');
        p = p.parentPartKey ? byKey.get(p.parentPartKey) : undefined;
    }
    return { partKey: targetKey, name: (target.name || '').trim() || 'Unnamed post', path };
}

/** What a linked post currently shows, given its represented post's active holders' names. */
export function linkSummary(holderNames: Array<string | null | undefined> | null | undefined): string {
    const names = (holderNames || []).filter((n): n is string => !!n && !!n.trim());
    return names.length ? `shows ${names.join(', ')}` : 'vacant — shows whoever holds it from above';
}

/**
 * Why a staff-pool drop onto `partKey` must be refused (it is a linked post — people are assigned on
 * the post it represents), or null when the drop may go ahead.
 */
export function linkedDropRefusal(orgParts: PartLike[] | null | undefined, partKey: string | undefined): string | null {
    const origin = linkOrigin(orgParts, partKey);
    if (!origin) return null;
    const self = (orgParts || []).find((p) => !!p && p.partKey === partKey);
    const name = (self?.name || '').trim() || 'This post';
    return `${name} is linked to ${origin.name} — assign people there.`;
}

/**
 * Every post object inside one board card (an ECO node's data): body and header posts at the
 * card, section and unit levels, plus the detached Board of Directors box. The board's own
 * held-from-above pass must hand ALL of them to `applyLinkedPosts` as link targets — the
 * Department 19 offices point at head/deputy posts and Council "Directors" at the board seat.
 */
export function postsOfCard(card: any): any[] {
    if (!card || typeof card !== 'object') return [];
    const out: any[] = [];
    const add = (list: any) => {
        for (const p of Array.isArray(list) ? list : []) if (p && typeof p === 'object') out.push(p);
    };
    add(card.postsInParent);
    add(card.headerPostsInParent);
    for (const g of Array.isArray(card.detachedGroups) ? card.detachedGroups : []) add(g?.posts);
    for (const s of Array.isArray(card.sectionsInDepartment) ? card.sectionsInDepartment : []) {
        add(s?.postsInSection);
        add(s?.headerPostsInSection);
        for (const u of Array.isArray(s?.unitsInSection) ? s.unitsInSection : []) {
            add(u?.postsInUnit);
            add(u?.headerPostsInUnit);
        }
    }
    for (const u of Array.isArray(card.unitsInDepartment) ? card.unitsInDepartment : []) {
        add(u?.postsInUnit);
        add(u?.headerPostsInUnit);
    }
    return out;
}
